import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { apiService } from '@/services/api';

/**
 * Custom hook for managing application settings
 *
 * UPDATED: Now uses apiService instead of direct axios calls
 * - Removed redundant Authorization headers
 * - Better error handling
 */
export const useSettings = (API) => {
  const [settings, setSettings] = useState({
    // Transfer settings
    transfer_price_one_way: 50.0,
    transfer_price_round_trip: 100.0,
    enable_transfer_arrival: true,
    enable_transfer_departure: true,
    enable_transfer_round_trip: true,
    transfer_arrival_message: "For your departure please attend the reservation 1 day before",

    // Payment settings
    enable_stripe: true,
    enable_nexi: false,
    enable_pay_on_arrival: true,
    enable_non_refundable: true,
    flexible_rate_percentage: 30,

    // Beds24 credentials
    beds24_api_key: '',
    beds24_prop_key: '',
    beds24_property_id: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch settings
   */
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get('/admin/settings');
      setSettings(prevSettings => ({
        ...prevSettings,
        ...response.data
      }));
    } catch (err) {
      setError(err);
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update settings
   * FIXED: No more redundant auth header!
   */
  const updateSettings = useCallback(async (updatedSettings) => {
    setLoading(true);

    try {
      const response = await apiService.put('/admin/settings', updatedSettings);
      setSettings(prevSettings => ({
        ...prevSettings,
        ...response.data
      }));
      toast.success('✅ Settings updated successfully');
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update payment settings
   */
  const updatePaymentSettings = useCallback(async (paymentSettings) => {
    return await updateSettings(paymentSettings);
  }, [updateSettings]);

  /**
   * Update Beds24 credentials
   * FIXED: No more redundant auth header!
   */
  const updateBeds24Credentials = useCallback(async (credentials) => {
    setLoading(true);

    try {
      await apiService.put('/admin/settings', credentials);
      toast.success('✅ Beds24 credentials updated successfully! Click "Restart Backend" to apply changes.');
      await fetchSettings();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update Beds24 credentials');
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  }, [fetchSettings]);

  /**
   * Restart backend
   * FIXED: No more redundant auth header!
   */
  const restartBackend = useCallback(async () => {
    setLoading(true);

    try {
      await apiService.post('/admin/restart-backend', {});
      toast.success('🔄 Backend restart initiated! The service will reconnect in a few seconds...');

      // Wait for restart to complete
      setTimeout(() => {
        setLoading(false);
        toast.success('✅ Backend restarted successfully! Beds24 credentials are now active.');
      }, 5000);

      return { success: true };
    } catch (err) {
      // If we get a network error, it might mean the backend is already restarting
      if (err.message.includes('Network Error') || err.code === 'ERR_NETWORK') {
        toast.success('🔄 Backend is restarting... Please wait a few seconds.');
        setTimeout(() => {
          setLoading(false);
          toast.success('✅ Backend should be ready now!');
        }, 5000);
        return { success: true };
      } else {
        toast.error(err.response?.data?.detail || 'Failed to restart backend');
        setLoading(false);
        return { success: false, error: err };
      }
    }
  }, []);

  /**
   * Export backup
   * FIXED: No more redundant auth header!
   */
  const exportBackup = useCallback(async () => {
    setLoading(true);

    try {
      const response = await apiService.get('/admin/export/all', { timeout: 120000 });

      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hotel-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Backup exported successfully! (${response.data.total_documents} documents)`);
      return { success: true };
    } catch (err) {
      console.error('Export error:', err);
      toast.error(err.response?.data?.detail || 'Failed to export backup');
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Import backup
   * FIXED: No more redundant auth header!
   */
  const importBackup = useCallback(async (backupFile) => {
    if (!backupFile) {
      toast.error('Please select a backup file first');
      return { success: false };
    }

    setLoading(true);

    try {
      // Read the file
      const fileText = await backupFile.text();
      const backupData = JSON.parse(fileText);

      // Validate backup format
      if (!backupData.data) {
        throw new Error('Invalid backup file format');
      }

      // Send to backend
      await apiService.post('/admin/backup/import', backupData);

      toast.success('✅ Backup restored successfully! Refreshing page...');

      // Refresh the page after 2 seconds to reload all data
      setTimeout(() => {
        window.location.reload();
      }, 2000);

      return { success: true };
    } catch (err) {
      console.error('Import error:', err);
      toast.error(err.response?.data?.detail || err.message || 'Failed to import backup');
      setLoading(false);
      return { success: false, error: err };
    }
  }, []);

  /**
   * Initial fetch
   */
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    loading,
    error,
    fetchSettings,
    updateSettings,
    updatePaymentSettings,
    updateBeds24Credentials,
    restartBackend,
    exportBackup,
    importBackup,
  };
};
